// CreativeCanvas — renders a branded social creative at full export resolution.
// Supports lightweight editing via `overrides` (drag offset, font scale, colors,
// overlay) and an `editable` mode that makes the text block draggable.
// Visually scaled for preview; html-to-image captures the inner node full-size.
import { forwardRef } from 'react';

export const SIZES = {
  '1:1':  { w: 1080, h: 1080, label: 'Square 1:1' },
  '4:5':  { w: 1080, h: 1350, label: 'Portrait 4:5' },
  '9:16': { w: 1080, h: 1920, label: 'Story/Reel 9:16' },
  '16:9': { w: 1920, h: 1080, label: 'Landscape 16:9' },
};

export const TEMPLATES = [
  { id: 'spotlight', name: 'Spotlight', desc: 'Full-bleed image, text bottom' },
  { id: 'card', name: 'Card', desc: 'Centered card on brand gradient' },
  { id: 'split', name: 'Split', desc: 'Image top, brand band bottom' },
];

export const DEFAULT_OVERRIDES = {
  tx: 0, ty: 0, headlineScale: 1, subScale: 1, textColor: null, accentColor: null, overlay: 0.78,
};

const FALLBACK_KIT = {
  primary_color: '#4f46e5', secondary_color: '#0ea5e9', accent_color: '#f59e0b',
  text_color: '#0f172a', font_family: 'Inter', logo_url: null,
};

export const CreativeCanvas = forwardRef(
  ({ template, size, kit, content, bgImage, overrides, editable = false, exporting = false, onDragStart }, ref) => {
    const { w, h } = SIZES[size] || SIZES['1:1'];
    const k = { ...FALLBACK_KIT, ...(kit || {}) };
    const ov = { ...DEFAULT_OVERRIDES, ...(overrides || {}) };
    const u = w / 1080;
    const font = k.font_family || 'Inter';
    const { headline = '', subheadline = '', cta = '' } = content || {};
    const accent = ov.accentColor || k.accent_color;

    const base = {
      width: w, height: h, position: 'relative', overflow: 'hidden',
      fontFamily: `${font}, system-ui, sans-serif`, boxSizing: 'border-box',
    };
    const Logo = ({ style }) => (k.logo_url ? (
      <img src={k.logo_url} alt="logo" crossOrigin="anonymous"
        style={{ height: 70 * u, objectFit: 'contain', ...style }} />
    ) : null);
    const ctaPill = (color) => (cta ? (
      <span style={{
        display: 'inline-block', background: accent, color, fontWeight: 700,
        fontSize: 30 * u, padding: `${16 * u}px ${36 * u}px`, borderRadius: 999 * u,
        marginTop: 28 * u, letterSpacing: 0.5,
      }}>{cta}</span>
    ) : null);

    // Wrapper applied to the movable text block: translate + (in edit mode) a
    // dashed grab affordance that is hidden during export.
    const editStyle = (editable && !exporting)
      ? { cursor: 'grab', outline: `${2 * u}px dashed rgba(255,255,255,0.65)`, outlineOffset: `${8 * u}px`, borderRadius: 8 * u }
      : {};
    const moveProps = (editable && !exporting) ? { onPointerDown: onDragStart, 'data-testid': 'creative-text-drag' } : {};
    const translate = { transform: `translate(${ov.tx}px, ${ov.ty}px)` };

    if (template === 'card') {
      return (
        <div ref={ref} style={{ ...base, background: `linear-gradient(135deg, ${k.primary_color}, ${k.secondary_color})`, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 80 * u }}>
          <div {...moveProps} style={{ background: '#ffffff', borderRadius: 40 * u, padding: 80 * u, width: '100%', textAlign: 'center', boxShadow: `0 ${30 * u}px ${80 * u}px rgba(0,0,0,0.25)`, ...translate, ...editStyle }}>
            <Logo style={{ marginBottom: 40 * u }} />
            <div style={{ color: ov.textColor || k.text_color, fontWeight: 800, fontSize: 84 * u * ov.headlineScale, lineHeight: 1.05, marginBottom: 28 * u }}>{headline}</div>
            <div style={{ color: '#475569', fontSize: 40 * u * ov.subScale, lineHeight: 1.3 }}>{subheadline}</div>
            {ctaPill('#ffffff')}
          </div>
        </div>
      );
    }

    if (template === 'split') {
      return (
        <div ref={ref} style={{ ...base, display: 'flex', flexDirection: 'column' }}>
          <div style={{ flex: '0 0 58%', background: bgImage ? `url(${bgImage}) center/cover` : `linear-gradient(135deg, ${k.secondary_color}, ${k.primary_color})`, position: 'relative' }}>
            <div style={{ position: 'absolute', top: 50 * u, left: 50 * u }}><Logo /></div>
          </div>
          <div style={{ flex: 1, background: k.primary_color, color: '#fff', padding: 70 * u, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div {...moveProps} style={{ ...translate, ...editStyle }}>
              <div style={{ color: ov.textColor || '#fff', fontWeight: 800, fontSize: 78 * u * ov.headlineScale, lineHeight: 1.05, marginBottom: 22 * u }}>{headline}</div>
              <div style={{ fontSize: 38 * u * ov.subScale, opacity: 0.92, lineHeight: 1.3 }}>{subheadline}</div>
              {ctaPill('#ffffff')}
            </div>
          </div>
        </div>
      );
    }

    // spotlight (default)
    return (
      <div ref={ref} style={{ ...base, background: bgImage ? `url(${bgImage}) center/cover` : `linear-gradient(135deg, ${k.primary_color}, ${k.secondary_color})` }}>
        <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.0) 35%, rgba(0,0,0,${ov.overlay}) 100%)` }} />
        <div style={{ position: 'absolute', top: 60 * u, left: 60 * u }}><Logo /></div>
        <div {...moveProps} style={{ position: 'absolute', left: 60 * u, right: 60 * u, bottom: 70 * u, color: '#fff', ...translate, ...editStyle }}>
          <div style={{ color: ov.textColor || '#fff', fontWeight: 800, fontSize: 92 * u * ov.headlineScale, lineHeight: 1.04, marginBottom: 24 * u, textShadow: `0 ${4 * u}px ${20 * u}px rgba(0,0,0,0.4)` }}>{headline}</div>
          <div style={{ fontSize: 42 * u * ov.subScale, opacity: 0.95, lineHeight: 1.3, maxWidth: '85%' }}>{subheadline}</div>
          {ctaPill('#ffffff')}
        </div>
      </div>
    );
  },
);

CreativeCanvas.displayName = 'CreativeCanvas';
export default CreativeCanvas;
